import React from 'react';
import styled, { css } from 'styled-components';
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { colors } from '../../const';
import { Card as CardBase, CardHeader } from './Components';
import { Typography } from '../typography';

type GateStatus = 'GATED_IN' | 'GATED_OUT';

interface TablePanelColumn<T> {
  head?: () => React.ReactNode;
  headText?: () => string;
  /** Выравнивание ячейки заголовка при использовании `headText` */
  headAlign?: TableCellProps['align'];
  body?: (row: T) => React.ReactNode;
  bodyText?: (row: T) => string;
  column?: number;
}

interface TablePanelProps<T> {
  title: string;
  viewAllLink?: string;
  data: T[];
  columns: TablePanelColumn<T>[];
}

/** Совпадает с горизонтальным padding карточки — нужен только для визуального фона TableHead. */
const CARD_INNER_PADDING = '1.6rem';

const Card = styled(CardBase)`
  overflow: hidden;
  padding: ${CARD_INNER_PADDING};
`;

const tablePanelContainerOverrides = css`
  box-shadow: none;
  border-radius: 0;
  width: 100%;
  overflow: visible;
`;

const tablePanelTableRoot = css`
  width: 100%;
  table-layout: auto;
`;

const tablePanelTheadCell = css`
  position: relative;
  padding: 1.2rem 0.8rem;
  background-color: ${colors['--gray-30']};
  border-top: 0.1rem solid ${colors['--gray-50']};
  border-bottom: 0.1rem solid ${colors['--gray-50']};
  height: 4.6rem;
  font-size: var(--font-size-2);
  line-height: var(--line-height-2);
  vertical-align: bottom;
`;

const tablePanelTheadCornerBleedPseudo = css`
  display: block;
  content: '';
  position: absolute;
  top: -0.1rem;
  bottom: -0.1rem;
  width: ${CARD_INNER_PADDING};
  height: auto;
  background-color: ${colors['--gray-30']};
  border-top: 0.1rem solid ${colors['--gray-50']};
  border-bottom: 0.1rem solid ${colors['--gray-50']};
  padding: 1.2rem 0;
  pointer-events: none;
  z-index: 0;
`;

const tablePanelTbodyCell = css`
  padding: 1.2rem 0.8rem;
  border-bottom: 0.1rem solid ${colors['--gray-50']};
  font-size: var(--font-size-2);
  line-height: var(--line-height-2);
  color: ${colors['--gray-90']};
`;

const StyledTableContainer = styled(TableContainer)`
  && {
    ${tablePanelContainerOverrides}
  }
  & .MuiTable-root {
    ${tablePanelTableRoot}
  }

  & tbody > tr:last-child > * {
    border-bottom: none;
  }
  & thead .MuiTableCell-root {
    ${tablePanelTheadCell}
  }
  & thead > tr > *:first-child,
  & thead > tr > *:last-child {
    position: relative;

    & > * {
      position: relative;
      z-index: 1;
    }
  }
  & thead > tr > *:first-child::before,
  & thead > tr > *:last-child::after {
    ${tablePanelTheadCornerBleedPseudo}
  }
  & thead > tr > *:first-child::before {
    left: calc(-1 * ${CARD_INNER_PADDING});
  }
  & thead > tr > *:last-child::after {
    right: calc(-1 * ${CARD_INNER_PADDING});
  }

  & tbody .MuiTableCell-root {
    ${tablePanelTbodyCell}
  }

  & tr > *:first-child {
    padding-left: 0;
  }
  & tr > *:last-child {
    padding-right: 0;
  }
`;

export const IconCircle = styled.div<{ $color: string }>`
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 999rem;
  border: 0.2rem solid ${({ $color }) => $color};
  color: ${({ $color }) => $color};
  display: grid;
  place-items: center;

  svg {
    font-size: 1.6rem;
  }
`;

export const TablePanel = <T extends { status: GateStatus }>({
  title,
  viewAllLink,
  data,
  columns,
}: TablePanelProps<T>) => {
  const showHeadRow = columns.some((col) => col.head || col.headText);

  const tableColumns = columns.map((item) => item.column || null);

  return (
    <Card>
      <CardHeader title={title} viewAllLink={viewAllLink} />

      <StyledTableContainer>
        <MuiTable size="small" aria-label="table-panel">
          <colgroup>
            {tableColumns.map((col, i) => (
              <col
                key={`table-col-${i}`}
                style={typeof col === 'number' ? { width: `${col + 2.4}rem` } : undefined}
              />
            ))}
          </colgroup>
          {showHeadRow ? (
            <TableHead>
              <TableRow>
                {columns.map((col, colIdx) => {
                  if (col.head)
                    return (
                      <React.Fragment key={`table-head-${colIdx}`}>{col.head()}</React.Fragment>
                    );
                  if (col.headText)
                    return (
                      <TableCell key={`table-head-${colIdx}`} align={col.headAlign ?? 'left'}>
                        <Typography variant={'textS'} color={'--gray-80'}>
                          {col.headText()}
                        </Typography>
                      </TableCell>
                    );
                  return <TableCell key={`table-head-${colIdx}`} align="left" />;
                })}
              </TableRow>
            </TableHead>
          ) : null}
          <TableBody>
            {data.map((row, rowIdx) => (
              <TableRow key={`table-row-${rowIdx}`}>
                {columns.map((col, colIdx) => {
                  if (col.body)
                    return (
                      <React.Fragment key={`cell-${rowIdx}-${colIdx}`}>
                        {col.body(row)}
                      </React.Fragment>
                    );
                  if (col.bodyText)
                    return (
                      <TableCell key={`cell-${rowIdx}-${colIdx}`} align="left">
                        {col.bodyText(row)}
                      </TableCell>
                    );
                  return <TableCell key={`cell-${rowIdx}-${colIdx}`} align="left" />;
                })}
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      </StyledTableContainer>
    </Card>
  );
};
